import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { NotificationService } from "core/notification.service";
import { ProgressService } from "core/progress.service";
import { TranslateChoiceService } from "core/translate/translate-choice.service";
import { Step } from "common/progress/round-progress.component";

@Component({
	selector: "stages-common-progress",
	templateUrl: "./progress.component.html",
	styleUrls: ["progress.component.scss"],
})
export class CommonProgressComponent implements OnInit, OnDestroy {
	@Input()
	set disableNextUntilDone(disableNextUntilDone: boolean) {
		this.nextDisabled = disableNextUntilDone;
	}

	@Input()
	operationName!: string;

	@Input()
	jobName!: string;

	@Input()
	workspaceId!: string;

	@Input()
	pv!: string;

	@Input()
	messageKeyPrefix?: string;

	@Input()
	download?: string | null;

	@Input()
	resumable?: boolean = true;

	@Input()
	stoppable: boolean = false;

	@Input()
	isPopup: boolean = false;

	@Input()
	hasNextButton: boolean = true;

	@Output() readonly closed = new EventEmitter<void>();

	@Output() readonly next = new EventEmitter<void>();

	@Output() readonly finished = new EventEmitter<stages.common.ProgressInfo>();

	@Output() readonly stopped = new EventEmitter<void>();

	finalState!: string;
	progress!: number;
	isWaiting?: boolean;
	remainingTimeMillis!: number;
	error?: string | null;
	incidentId?: string | null;
	warnings!: string[];
	warningsMessageKey!: string;
	nextDisabled: boolean = false;
	step: Step | null = null;
	currentStepNr: number = 0;
	amountOfSteps: number = 0;
	attempts: number = 0;
	maxAttempts: number = 1;
	nextExecutionTime?: string;
	retryIfFailed: boolean = false;
	lastExecutionFailed: boolean = false;

	private pollingHandle?: number;

	constructor(
		private notificationService: NotificationService,
		private progressService: ProgressService,
		private translateChoiceService: TranslateChoiceService,
	) {}

	async ngOnInit(): Promise<void> {
		const progressInfo = await this.getProgress(true);
		this.onProgressUpdate(progressInfo);

		this.pollingHandle = this.notificationService.pollPromise(
			async () => this.getProgress(true),
			(progress: stages.common.ProgressInfo) => {
				this.onProgressUpdate(progress);
				if (this.finalState) {
					this.notificationService.cancel(this.pollingHandle!);
				}
			},
		);
	}

	ngOnDestroy(): void {
		if (this.pollingHandle) {
			this.notificationService.cancel(this.pollingHandle);
			this.pollingHandle = undefined;
		}
	}

	hasWarnings(): boolean {
		return this.finalState === "warnings";
	}

	async getProgress(isPolling: boolean): Promise<stages.common.ProgressInfo> {
		return this.progressService.getJobProgress(this.operationName, this.workspaceId, this.pv, this.jobName, isPolling);
	}

	onProgressUpdate(progress: stages.common.ProgressInfo): void {
		this.isWaiting = progress.jobStatus === "WAITING";
		this.progress = progress.value;
		this.currentStepNr = progress.currentStepPosition;
		this.step = progress.currentStep;
		this.amountOfSteps = progress.amountOfSteps;
		this.remainingTimeMillis = progress.remainingTimeMillis;
		this.attempts = progress.attempts;
		this.maxAttempts = progress.maxAttempts;
		this.nextExecutionTime = progress.nextExecutionTime === null ? undefined : progress.nextExecutionTime;
		this.retryIfFailed = progress.retryIfFailed;
		this.lastExecutionFailed = progress.lastJobStatus !== null && progress.lastJobStatus === "COMPLETED_FAILED";
		switch (progress.jobStatus) {
			case "COMPLETED_SUCCESSFUL": {
				this.finalState = "success";
				this.nextDisabled = false;
				this.finished.emit(progress);
				if (!this.hasNextButton) this.closed.emit();
				break;
			}

			case "COMPLETED_FAILED": {
				this.finalState = "failure";
				this.error = progress.errorMessage;
				this.incidentId = progress.incidentId;
				this.finished.emit(progress);
				break;
			}

			case "COMPLETED_WITH_WARNINGS": {
				this.finalState = "warnings";
				this.warnings = progress.warnings;
				this.warningsMessageKey = this.translateChoiceService.getKey(
					{ length: this.warnings.length },
					"progress.warnings.count.{length}",
				);
				this.finished.emit(progress);
				break;
			}

			default: {
				// nothing to do
				break;
			}
		}
	}
}
